import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useIntl } from 'react-intl';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { Table } from 'components/common';
import { FormModal } from 'components/common/modals';
import { DynoFormModal } from 'components/dyno-template/modals';
import SwiftSearchSection from 'components/transfer/swift/TextfieldSection';

import {
  addNewFormFieldHandler,
  formTitleHandler,
  addNewDynamicFieldHandler,
  editFormFieldHandler,
  submitActionHandler,
} from 'utils/favourite/transfer/handler';
import { getSwiftBanks } from 'utils/transfer/swift/getter';

import { SWIFT_TABLE_HEADERS } from 'settings/constants/transfer/swift';
import { FAVOURITE_SWIFT_PAGE_SIZE, MODAL_FULLSCREEN_SIZE } from 'settings/constants/ui-control';

const FavouriteModal = ({
  onChange,
  formDetail,
  onSwiftCodeClick,
  isSwiftBankSearch,
  onSubmitFavourite,
  favouriteSwiftBanks,
  onAddFavouriteClose,
  isFromTransferResult,
  isAddNewDetailSuccess,
  isFavouriteModalOpen,
  maintenanceTransfers,
  favouriteDetail,
  favouriteTransferType,
  isEditFavouriteDetailSuccess,
}) => {
  const { formatMessage } = useIntl();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(MODAL_FULLSCREEN_SIZE));

  const formTitle = formTitleHandler(isAddNewDetailSuccess, favouriteTransferType, isSwiftBankSearch).concat(
    isEditFavouriteDetailSuccess ? 'Edit' : ''
  );
  const formFields =
    isFromTransferResult || isEditFavouriteDetailSuccess
      ? editFormFieldHandler(isAddNewDetailSuccess, favouriteTransferType)
      : addNewFormFieldHandler(isAddNewDetailSuccess, favouriteTransferType, maintenanceTransfers);
  const dynamicFields = addNewDynamicFieldHandler(favouriteTransferType, favouriteDetail, isFromTransferResult);

  const swiftBanks = getSwiftBanks(favouriteSwiftBanks, onSwiftCodeClick);

  const handleSubmitFavourite = (swiftBankPayload, isSwiftTransfer = false) =>
    onSubmitFavourite({
      swiftBankPayload,
      action: submitActionHandler(isAddNewDetailSuccess, isSwiftTransfer, isEditFavouriteDetailSuccess),
    });

  const handleSwiftFavouriteClose = () => {
    onAddFavouriteClose();
    onChange({ isSwiftBankSearch: false });
  };

  return (
    <>
      {isSwiftBankSearch ? (
        <FormModal
          fullWidth
          maxWidth="lg"
          fullScreen={fullScreen}
          hasContentHeader={false}
          isOpen={isFavouriteModalOpen}
          onClose={handleSwiftFavouriteClose}
          title={formTitle && formatMessage({ id: formTitle })}
        >
          <div className="pa4">
            <SwiftSearchSection onSearchBank={searchDetail => handleSubmitFavourite(searchDetail, true)} hasModalStyle />
            {!isEmpty(swiftBanks) && (
              <Table
                hasPagination
                className="pt4"
                rows={swiftBanks}
                pageSize={FAVOURITE_SWIFT_PAGE_SIZE}
                columns={SWIFT_TABLE_HEADERS(formatMessage)}
              />
            )}
          </div>
        </FormModal>
      ) : (
        <DynoFormModal
          isBasicButton
          title={formTitle}
          fields={formFields}
          onChange={onChange}
          hasNoteContent={false}
          formDetail={formDetail}
          isOpen={isFavouriteModalOpen}
          onClose={onAddFavouriteClose}
          dynamicFields={dynamicFields}
          onSubmit={handleSubmitFavourite}
          actionButtonTitle="buttonText.continue"
        />
      )}
    </>
  );
};

FavouriteModal.defaultProps = {
  onChange: null,
  favouriteSwiftBanks: {},
  favouriteTransferType: '',
  isFromTransferResult: false,
  isSwiftBankSearch: false,
  onSwiftCodeClick: null,
};

FavouriteModal.propTypes = {
  onChange: PropTypes.func,
  onSwiftCodeClick: PropTypes.func,
  isSwiftBankSearch: PropTypes.bool,
  isFromTransferResult: PropTypes.bool,
  favouriteSwiftBanks: PropTypes.object,
  formDetail: PropTypes.object.isRequired,
  favouriteDetail: PropTypes.object.isRequired,
  onSubmitFavourite: PropTypes.func.isRequired,
  onAddFavouriteClose: PropTypes.func.isRequired,
  isFavouriteModalOpen: PropTypes.bool.isRequired,
  isAddNewDetailSuccess: PropTypes.bool.isRequired,
  isEditFavouriteDetailSuccess: PropTypes.bool.isRequired,
  favouriteTransferType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maintenanceTransfers: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
};

export default FavouriteModal;
